/**
 * Header - Top navigation bar with logo, progress, and settings.
 */

import { memo, useState, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Settings, X, BookOpen, Moon, Sun, Bug, Play } from 'lucide-react'
import { cn } from '../../lib/utils'
import { SimpleProgressBar } from '../curriculum/ProgressBar'
import type { Lesson } from '../../curriculum/manager'
import { ToastContainer } from '../ui/Toast'
import { useToast } from '../ui/useToast'
import { submitBugReport, BugReportSubmissionError } from '../../lib/bugReports'
import { BugReportModal, type BugReportSubmission } from './BugReportModal'

export interface HeaderProps {
  /** Current lesson title */
  currentLessonTitle?: string
  /** Current lesson number (1-based) */
  currentLessonNumber?: number
  /** Total number of lessons */
  totalLessons?: number
  /** Progress percentage (0-100) */
  progress?: number
  /** Current visualization mode */
  visualizationMode?: 'beginner' | 'advanced'
  /** Callback to toggle visualization mode */
  onToggleMode?: () => void
  /** Debug mode enabled */
  debugMode?: boolean
  /** Callback to toggle debug mode */
  onToggleDebugMode?: () => void
  /** All lessons for debug selector */
  allLessons?: Lesson[]
  /** Callback to skip to a lesson */
  onSkipToLesson?: (lessonId: string) => void
  /** Optional className */
  className?: string
}

/**
 * Header displays the app title, progress, and settings.
 */
export const Header = memo(function Header({
  currentLessonTitle,
  currentLessonNumber,
  totalLessons,
  progress = 0,
  visualizationMode = 'beginner',
  onToggleMode,
  debugMode = false,
  onToggleDebugMode,
  allLessons = [],
  onSkipToLesson,
  className,
}: HeaderProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isBugReportOpen, setIsBugReportOpen] = useState(false)
  const [isBugReportSubmitting, setIsBugReportSubmitting] = useState(false)
  const [bugReportError, setBugReportError] = useState<string | null>(null)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark')
    }
    return false
  })
  const { toasts, addToast, dismissToast } = useToast()

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen((prev) => !prev)
  }, [])

  const openBugReport = useCallback(() => {
    setBugReportError(null)
    setIsBugReportOpen(true)
  }, [])

  const closeBugReport = useCallback(() => {
    if (isBugReportSubmitting) return
    setIsBugReportOpen(false)
    setBugReportError(null)
  }, [isBugReportSubmitting])

  const handleBugReportSubmit = useCallback(async (payload: BugReportSubmission) => {
    setBugReportError(null)
    setIsBugReportSubmitting(true)

    try {
      const result = await submitBugReport({
        title: payload.title,
        description: payload.description,
        reporterEmail: payload.reporterEmail,
        screenshots: payload.screenshots,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      })

      setIsBugReportOpen(false)
      addToast({
        title: result.emailSent ? 'Bug report submitted' : 'Bug report saved',
        message: result.emailSent
          ? 'Thanks for the report. Support has been notified.'
          : 'Report saved, but email notification failed. Support can still review it in the database.',
        severity: result.emailSent ? 'success' : 'warning',
      })
    } catch (error) {
      const message = error instanceof BugReportSubmissionError
        ? error.message
        : 'Could not submit bug report. Please try again.'
      setBugReportError(message)
      addToast({
        title: 'Unable to submit bug report',
        message,
        severity: 'error',
      })
    } finally {
      setIsBugReportSubmitting(false)
    }
  }, [addToast])

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode((prev) => {
      const newValue = !prev
      if (newValue) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
      return newValue
    })
  }, [])

  // Keyboard shortcut: Ctrl+Shift+D to toggle debug mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault()
        onToggleDebugMode?.()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onToggleDebugMode])

  return (
    <>
      <header
        className={cn(
          'sticky top-0 z-50 border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80',
          className
        )}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center justify-between gap-4">
            {/* Logo and title */}
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                <BookOpen className="h-4 w-4" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-sm font-semibold text-foreground">
                  Learn Prediction Markets
                </h1>
              </div>
            </div>

            {/* Progress bar (hidden on mobile) */}
            <div className="hidden flex-1 max-w-xs md:block">
              <SimpleProgressBar
                progress={progress}
                label={
                  totalLessons
                    ? `Lesson ${currentLessonNumber || 1} of ${totalLessons}`
                    : undefined
                }
              />
            </div>

            {/* Current lesson indicator */}
            {currentLessonTitle && (
              <div className="hidden items-center gap-2 lg:flex">
                <span className="text-sm text-muted-foreground">Current:</span>
                <span className="max-w-[200px] truncate text-sm font-medium text-foreground">
                  {currentLessonTitle}
                </span>
              </div>
            )}

            {/* Sandbox link */}
            <Link
              to="/sandbox"
              className="flex items-center gap-2 rounded-md bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20"
            >
              <Play className="h-4 w-4" />
              <span className="hidden sm:inline">Sandbox</span>
            </Link>

            <div className="flex items-center gap-2">
              {/* Bug report button */}
              <button
                type="button"
                onClick={openBugReport}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                  'text-muted-foreground hover:bg-muted hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
                )}
                aria-label="Report a bug"
              >
                <Bug className="h-5 w-5" aria-hidden="true" />
              </button>

              {/* Settings button */}
              <button
                type="button"
                onClick={toggleSettings}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                  'text-muted-foreground hover:bg-muted hover:text-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
                )}
                aria-label="Settings"
                aria-expanded={isSettingsOpen}
              >
                {isSettingsOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Settings className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile progress bar */}
          <div className="pb-2 md:hidden">
            <SimpleProgressBar
              progress={progress}
              label={
                totalLessons
                  ? `Lesson ${currentLessonNumber || 1} of ${totalLessons}`
                  : undefined
              }
            />
          </div>
        </div>

        {/* Settings panel */}
        {isSettingsOpen && (
          <div className="border-t border-border bg-card">
            <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {/* Visualization mode toggle */}
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Visualization Mode
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {visualizationMode === 'beginner'
                        ? 'Simplified view with explanations'
                        : 'Traditional trading interface'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onToggleMode}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                      visualizationMode === 'beginner'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    )}
                  >
                    {visualizationMode === 'beginner' ? 'Beginner' : 'Advanced'}
                  </button>
                </div>

                {/* Dark mode toggle */}
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Theme
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {isDarkMode ? 'Dark mode active' : 'Light mode active'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleDarkMode}
                    className="flex h-9 w-9 items-center justify-center rounded-md bg-muted text-foreground transition-colors hover:bg-muted/80"
                    aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                  >
                    {isDarkMode ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Debug mode toggle */}
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Debug Mode
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Skip to any lesson (Ctrl+Shift+D)
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onToggleDebugMode}
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-md transition-colors',
                      debugMode
                        ? 'bg-amber-500 text-white'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                    )}
                    aria-label={debugMode ? 'Disable debug mode' : 'Enable debug mode'}
                  >
                    <Bug className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Debug lesson selector */}
              {debugMode && allLessons.length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3">
                  <p className="mb-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    Skip to Lesson (Debug)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {allLessons.map((lesson, idx) => (
                      <button
                        key={lesson.id}
                        type="button"
                        onClick={() => onSkipToLesson?.(lesson.id)}
                        className={cn(
                          'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                          currentLessonNumber === idx + 1
                            ? 'bg-amber-500 text-white'
                            : 'bg-muted text-foreground hover:bg-amber-500/20'
                        )}
                      >
                        {idx + 1}. {lesson.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <BugReportModal
        isOpen={isBugReportOpen}
        isSubmitting={isBugReportSubmitting}
        submitError={bugReportError}
        onClose={closeBugReport}
        onSubmit={handleBugReportSubmit}
      />

      <ToastContainer
        toasts={toasts}
        onDismiss={dismissToast}
      />
    </>
  )
})
